import * as THREE from 'three';

export type QualityTier = 'low' | 'medium' | 'high' | 'ultra';

export interface QualitySettings {
  tier: QualityTier;
  /** Plafond du pixel ratio (l'adaptatif descend en dessous si besoin). */
  dprMax: number;
  msaa: number;
  shadowMapSize: number;
  shadowRadius: number;
  ssao: boolean;
  dof: boolean;
  bloom: boolean;
  dust: boolean;
}

export const QUALITY: Record<QualityTier, QualitySettings> = {
  low:    { tier: 'low',    dprMax: 1.0,  msaa: 0, shadowMapSize: 1024, shadowRadius: 3, ssao: false, dof: false, bloom: true, dust: false },
  medium: { tier: 'medium', dprMax: 1.25, msaa: 2, shadowMapSize: 1024, shadowRadius: 4, ssao: false, dof: true,  bloom: true, dust: true },
  high:   { tier: 'high',   dprMax: 1.5,  msaa: 4, shadowMapSize: 2048, shadowRadius: 6, ssao: true,  dof: true,  bloom: true, dust: true },
  ultra:  { tier: 'ultra',  dprMax: 2.0,  msaa: 4, shadowMapSize: 4096, shadowRadius: 6, ssao: true,  dof: true,  bloom: true, dust: true },
};

/** Devine un palier de qualité initial à partir de l'appareil et du GPU. */
export function detectTier(gl: WebGL2RenderingContext): QualityTier {
  const ua = navigator.userAgent;
  const mobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(ua));
  let gpu = '';
  const ext = gl.getExtension('WEBGL_debug_renderer_info');
  if (ext) gpu = String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL));
  if (mobile) return /Apple GPU|Adreno \(TM\) 7|Mali-G7[1-9]|Immortalis/i.test(gpu) ? 'medium' : 'low';
  if (/RTX|RX [67]\d{3}|Radeon Pro|Apple M[2-9]|Arc/i.test(gpu)) return 'ultra';
  if (/GTX|Radeon|Apple M1|Iris Xe/i.test(gpu)) return 'high';
  if (/Intel|SwiftShader|llvmpipe|Microsoft Basic/i.test(gpu)) return 'medium';
  return 'high';
}

/**
 * Renderer WebGL2 + résolution adaptative : on vise ~60 fps en ajustant le pixel ratio
 * (la scène reste nette, seul le suréchantillonnage varie).
 */
export class Renderer {
  readonly gl: THREE.WebGLRenderer;
  quality: QualitySettings;
  dpr: number;
  private frameTimes: number[] = [];
  private lastAdapt = 0;
  // Chronométrage GPU (EXT_disjoint_timer_query_webgl2) : mesure la vraie charge, insensible au throttling.
  private timerExt: { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number } | null = null;
  private pendingQueries: WebGLQuery[] = [];
  private activeQuery: WebGLQuery | null = null;
  /** Dernier temps GPU mesuré (ms), ou -1. */
  gpuMs = -1;
  lastGpuMs = -1;
  onResize: (w: number, h: number, dpr: number) => void = () => {};

  constructor(private container: HTMLElement) {
    this.gl = new THREE.WebGLRenderer({
      antialias: false,           // l'AA est géré par le composer (MSAA/SMAA)
      powerPreference: 'high-performance',
      stencil: false,
      depth: true,
      alpha: true,                // transparent en réalité augmentée (le fond reste opaque à l'écran : clearColor alpha 1)
      preserveDrawingBuffer: false,
    });
    this.gl.outputColorSpace = THREE.SRGBColorSpace;
    this.gl.toneMapping = THREE.NoToneMapping; // tone mapping en post-process (AgX)
    this.gl.shadowMap.enabled = true;
    this.gl.shadowMap.type = THREE.PCFShadowMap;
    this.gl.setClearColor(0x0b0809, 1);
    container.appendChild(this.gl.domElement);

    this.timerExt = (this.gl.getContext() as WebGL2RenderingContext).getExtension('EXT_disjoint_timer_query_webgl2');
    const tier = (new URLSearchParams(location.search).get('q') as QualityTier) || detectTier(this.gl.getContext() as WebGL2RenderingContext);
    this.quality = QUALITY[tier] ?? QUALITY.high;
    this.dpr = Math.min(window.devicePixelRatio || 1, this.quality.dprMax);
    window.addEventListener('resize', () => this.resize());
  }

  setQuality(tier: QualityTier) {
    this.quality = QUALITY[tier];
    this.dpr = Math.min(window.devicePixelRatio || 1, this.quality.dprMax);
    this.resize();
  }

  resize() {
    const w = this.container.clientWidth, h = this.container.clientHeight;
    this.gl.setPixelRatio(this.dpr);
    this.gl.setSize(w, h, false);
    this.onResize(w, h, this.dpr);
  }

  beginGpuTimer() {
    const ext = this.timerExt;
    if (!ext || this.activeQuery || this.pendingQueries.length > 4) return;
    const gl = this.gl.getContext() as WebGL2RenderingContext;
    const q = gl.createQuery();
    if (!q) return;
    gl.beginQuery(ext.TIME_ELAPSED_EXT, q);
    this.activeQuery = q;
  }

  endGpuTimer() {
    const ext = this.timerExt;
    const gl = this.gl.getContext() as WebGL2RenderingContext;
    if (ext && this.activeQuery) {
      gl.endQuery(ext.TIME_ELAPSED_EXT);
      this.pendingQueries.push(this.activeQuery);
      this.activeQuery = null;
    }
    // Récupère les résultats disponibles (quelques frames de latence).
    while (this.pendingQueries.length) {
      const q = this.pendingQueries[0];
      if (!gl.getQueryParameter(q, gl.QUERY_RESULT_AVAILABLE)) break;
      const disjoint = gl.getParameter(ext!.GPU_DISJOINT_EXT);
      const ns = gl.getQueryParameter(q, gl.QUERY_RESULT) as number;
      gl.deleteQuery(q);
      this.pendingQueries.shift();
      if (!disjoint) { this.gpuMs = ns / 1e6; this.lastGpuMs = this.gpuMs; }
    }
  }

  /** À appeler chaque frame avec la durée de frame (ms). Ajuste le pixel ratio par paliers doux. */
  adapt(frameMs: number, now: number) {
    // Page en arrière-plan / sans focus : le navigateur bride rAF et le GPU baisse ses fréquences,
    // les mesures ne reflètent pas la vraie charge → on n'adapte pas.
    if (document.visibilityState !== 'visible' || !document.hasFocus()) { this.frameTimes.length = 0; this.lastAdapt = now; return; }
    if (this.timerExt) return this.adaptGpu(now, frameMs);
    const ft = this.frameTimes;
    // Onglet masqué, chargement, GC… : ces frames ne disent rien de la charge GPU.
    if (frameMs > 45 || document.visibilityState !== 'visible') { ft.length = 0; this.lastAdapt = now; return; }
    ft.push(frameMs);
    if (ft.length > 90) ft.shift();
    if (now - this.lastAdapt < 1500 || ft.length < 60) return;
    const sorted = [...ft].sort((a, b) => a - b);
    const p75 = sorted[Math.floor(sorted.length * 0.75)];
    const max = Math.min(window.devicePixelRatio || 1, this.quality.dprMax);
    let next = this.dpr;
    if (p75 > 19.5) next = Math.max(0.6, this.dpr - 0.15);
    else if (p75 < 13 && this.dpr < max) next = Math.min(max, this.dpr + 0.1);
    if (Math.abs(next - this.dpr) > 0.01) {
      this.dpr = next;
      this.resize();
      ft.length = 0;
    }
    this.lastAdapt = now;
  }

  /** Variante GPU : vise ≤ 10 ms de GPU (marge pour 60 fps et le CPU), remonte si < 5 ms. */
  private adaptGpu(now: number, frameMs: number) {
    const ft = this.frameTimes;
    if (this.gpuMs < 0) return;
    ft.push(this.gpuMs);
    this.gpuMs = -1;
    if (ft.length > 60) ft.shift();
    if (now - this.lastAdapt < 1200 || ft.length < 30) return;
    const sorted = [...ft].sort((a, b) => a - b);
    const p75 = sorted[Math.floor(sorted.length * 0.75)];
    const max = Math.min(window.devicePixelRatio || 1, this.quality.dprMax);
    let next = this.dpr;
    // On ne baisse que si l'image ralentit vraiment (sinon le GPU est juste en basse fréquence).
    if (p75 > 10 && frameMs > 18) next = Math.max(0.6, this.dpr * Math.sqrt(9 / p75));
    else if (p75 < 5 && this.dpr < max) next = Math.min(max, this.dpr + 0.15);
    if (Math.abs(next - this.dpr) > 0.02) {
      this.dpr = Math.round(next * 100) / 100;
      this.resize();
      ft.length = 0;
    }
    this.lastAdapt = now;
  }
}
